// src/pages/Account.js
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

function Account() {
  const navigate = useNavigate();

  // 1) Grab the user (and token) from localStorage
  const storedUser = localStorage.getItem('user');
  const userObj = storedUser ? JSON.parse(storedUser) : null;
  const token = userObj?.token || '';

  // -------------------------------------------
  // State for account data
  // -------------------------------------------
  const [accountData, setAccountData] = useState({
    created_at: '',
    last_login_timestamp: '',
    last_login_ip: '',
    username: '',
    email: '',
    fullname: '',
    timezone: '',
    datetime_format: '',
    notes: '',
    role: ''
  });

  // Assigned IPs
  const [assignedIPs, setAssignedIPs] = useState([]);
  const [totalIPs, setTotalIPs] = useState(0);

  // For new IP (admin-only)
  const [newIP, setNewIP] = useState('');

  // Tabs
  const [activeTab, setActiveTab] = useState('details');

  // Simple popup to confirm saves
  const [showSavePopup, setShowSavePopup] = useState(false);
  const closePopup = () => setShowSavePopup(false);

  // -------------------------------------------
  // Fetch: GET /api/v1/account
  // -------------------------------------------
  const fetchAccountDetails = useCallback(async () => {
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch account details.');
        return;
      }

      const data = await response.json();
      setAccountData({
        created_at: data.created_at,
        last_login_timestamp: data.last_login_timestamp,
        last_login_ip: data.last_login_ip,
        username: data.username,
        email: data.email,
        fullname: data.fullname,
        timezone: data.timezone,
        datetime_format: data.datetime_format,
        notes: data.notes,
        role: data.role
      });
    } catch (error) {
      console.error('Error fetching account details:', error);
    }
  }, [token]);

  // -------------------------------------------
  // Fetch: GET /api/v1/account/ips
  // -------------------------------------------
  const fetchAssignedIPs = useCallback(async () => {
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account/ips',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch assigned IPs.');
        return;
      }

      const data = await response.json();
      setAssignedIPs(data.ip_addresses);
      setTotalIPs(data.totalIPs);
    } catch (error) {
      console.error('Error fetching assigned IPs:', error);
    }
  }, [token]);

  // -------------------------------------------
  // PATCH /api/v1/account
  // -------------------------------------------
  async function handleSaveAccountChanges(event) {
    event.preventDefault();
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            email: accountData.email,
            timezone: accountData.timezone,
            datetime_format: accountData.datetime_format,
            notes: accountData.notes,
            fullname: accountData.fullname
          })
        }
      );

      if (!response.ok) {
        console.error('Error updating account data.');
        return;
      }

      const result = await response.json();
      console.log('Account updated:', result.message);

      // OPTIONAL: re-fetch to show the new data
      await fetchAccountDetails();

      // Show success popup
      setShowSavePopup(true);

      // If you want to also update localStorage's "fullname" so the UI shows it:
      if (storedUser) {
        const updatedUserObj = { ...userObj, fullname: accountData.fullname };
        localStorage.setItem('user', JSON.stringify(updatedUserObj));
      }
    } catch (error) {
      console.error('Error in handleSaveAccountChanges:', error);
    }
  }

  // -------------------------------------------
  // Add IP (admin-only)
  // -------------------------------------------
  async function handleAddIP() {
    if (accountData.role !== 'admin') {
      console.error('Only admins can add IPs.');
      return;
    }
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account/ips',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ip_address: newIP })
        }
      );

      if (response.status === 403) {
        console.error('Forbidden: user is not an admin.');
        return;
      }

      if (!response.ok) {
        console.error('Error adding new IP.');
        return;
      }

      setNewIP('');
      fetchAssignedIPs();
    } catch (error) {
      console.error('Error in handleAddIP:', error);
    }
  }

  // -------------------------------------------
  // Remove IP (admin-only)
  // -------------------------------------------
  async function handleRemoveIP(ip) {
    if (accountData.role !== 'admin') {
      console.error('Only admins can remove IPs.');
      return;
    }
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account/ips',
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ip_address: ip })
        }
      );

      if (response.status === 403) {
        console.error('Forbidden: user is not an admin.');
        return;
      }

      if (!response.ok) {
        console.error('Error removing IP.');
        return;
      }

      fetchAssignedIPs();
    } catch (error) {
      console.error('Error in handleRemoveIP:', error);
    }
  }

  // Helper
  const isAdmin = accountData.role === 'admin';

  // -------------------------------------------
  // useEffect: fetch data once on mount
  // -------------------------------------------
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    // Call both GETs once
    fetchAccountDetails();
    fetchAssignedIPs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  // -------------------------------------------
  // RENDER
  // -------------------------------------------
  return (
    <Layout>
      <div className="p-8">
        <h1 className="text-4xl font-bold text-white mb-4">My Account</h1>

        <div className="bg-dark-card p-4 rounded-lg mb-4">
          <div className="flex flex-col md:flex-row md:justify-between text-white">
            <div className="mb-2 md:mb-0">
              <p>
                Creation Date:{' '}
                {accountData.created_at
                  ? new Date(accountData.created_at).toLocaleString()
                  : '...'}
              </p>
              <p>
                Last Login:{' '}
                {accountData.last_login_timestamp
                  ? new Date(accountData.last_login_timestamp).toLocaleString()
                  : '...'}
              </p>
            </div>
            <div>
              <p>Last IP: {accountData.last_login_ip || '...'}</p>
              <p>Assigned IPs: {totalIPs}</p>
              <p>Role: {accountData.role || '...'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-2 rounded transition-colors duration-300 ${
              activeTab === 'details'
                ? 'bg-custom-gray text-white'
                : 'bg-dark-card text-white'
            }`}
          >
            Account Details
          </button>
          <button
            onClick={() => setActiveTab('ssh')}
            className={`px-4 py-2 rounded transition-colors duration-300 ${
              activeTab === 'ssh'
                ? 'bg-custom-gray text-white'
                : 'bg-dark-card text-white'
            }`}
          >
            SSH Keys
          </button>
          <button
            onClick={() => setActiveTab('api')}
            className={`px-4 py-2 rounded transition-colors duration-300 ${
              activeTab === 'api'
                ? 'bg-custom-gray text-white'
                : 'bg-dark-card text-white'
            }`}
          >
            API Keys
          </button>
          <button
            onClick={() => setActiveTab('login')}
            className={`px-4 py-2 rounded transition-colors duration-300 ${
              activeTab === 'login'
                ? 'bg-custom-gray text-white'
                : 'bg-dark-card text-white'
            }`}
          >
            Login History
          </button>
          <button
            onClick={() => setActiveTab('access')}
            className={`px-4 py-2 rounded transition-colors duration-300 ${
              activeTab === 'access'
                ? 'bg-custom-gray text-white'
                : 'bg-dark-card text-white'
            }`}
          >
            Access Log
          </button>
        </div>

        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Pane: Form */}
            <div className="bg-dark-card p-4 rounded-lg lg:col-span-2">
              <form
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                onSubmit={handleSaveAccountChanges}
              >
                {/* Fullname (editable) */}
                <div>
                  <label className="text-white block mb-1">Client Name</label>
                  <input
                    type="text"
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    value={accountData.fullname}
                    onChange={(e) =>
                      setAccountData({
                        ...accountData,
                        fullname: e.target.value
                      })
                    }
                    placeholder="Client Full Name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="text-white block mb-1">Mail Address</label>
                  <input
                    type="email"
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    value={accountData.email}
                    onChange={(e) =>
                      setAccountData({ ...accountData, email: e.target.value })
                    }
                    placeholder="Email address"
                  />
                </div>

                {/* Username (readonly) */}
                <div>
                  <label className="text-white block mb-1">Username</label>
                  <input
                    type="text"
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    value={accountData.username}
                    disabled
                  />
                </div>

                {/* Password (placeholder) */}
                <div>
                  <label className="text-white block mb-1">Password</label>
                  <input
                    type="password"
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    placeholder="(Use a separate form to change password)"
                    disabled
                  />
                </div>

                {/* Timezone */}
                <div>
                  <label className="text-white block mb-1">Timezone</label>
                  <select
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    value={accountData.timezone}
                    onChange={(e) =>
                      setAccountData({
                        ...accountData,
                        timezone: e.target.value
                      })
                    }
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Los_Angeles">
                      America/Los_Angeles
                    </option>
                    <option value="Europe/London">Europe/London</option>
                  </select>
                </div>

                {/* Datetime Format */}
                <div>
                  <label className="text-white block mb-1">Datetime Format</label>
                  <input
                    type="text"
                    className="w-full rounded p-2"
                    style={{ backgroundColor: '#272E3B', color: 'white' }}
                    value={accountData.datetime_format}
                    onChange={(e) =>
                      setAccountData({
                        ...accountData,
                        datetime_format: e.target.value
                      })
                    }
                    placeholder="MM/DD/YYYY HH:mm:ss"
                  />
                </div>

                {/* Save Button */}
                <div className="md:col-span-2 flex justify-end mt-4">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                  >
                    Save Account
                  </button>
                </div>
              </form>
            </div>

            {/* Right Pane: Notes + Assigned IPs */}
            <div className="flex flex-col gap-4">
              {/* Notes */}
              <div className="bg-dark-card p-4 rounded-lg">
                <label className="text-white block mb-1">
                  Enter account notes (only visible to you)
                </label>
                <textarea
                  className="w-full rounded p-2 mt-1"
                  style={{ backgroundColor: '#272E3B', color: 'white' }}
                  rows={5}
                  value={accountData.notes}
                  onChange={(e) =>
                    setAccountData({ ...accountData, notes: e.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={(e) => {
                    // We'll reuse the same PATCH call
                    handleSaveAccountChanges(e);
                  }}
                  className="bg-custom-gray text-white px-4 py-2 rounded mt-2 hover:bg-black transition duration-300"
                >
                  Save Notes
                </button>
              </div>

              {/* Assigned IPs */}
              <div className="bg-dark-card p-4 rounded-lg">
                <h2 className="text-white text-xl font-bold mb-2">
                  Assigned IPs
                </h2>
                <p className="text-white mb-4">Total IPs: {totalIPs}</p>

                {/* IP List */}
                <ul className="text-white mb-4">
                  {assignedIPs.map((ip, idx) => (
                    <li key={idx} className="flex justify-between mb-2">
                      <span>{ip}</span>
                      {/* If admin and IP !== '...', show remove button */}
                      {isAdmin && ip !== '...' && (
                        <button
                          onClick={() => handleRemoveIP(ip)}
                          className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 transition"
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Add New IP (admin-only) */}
                {isAdmin && (
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Enter new IP"
                      className="w-full rounded p-2 mr-2"
                      style={{ backgroundColor: '#272E3B', color: 'white' }}
                      value={newIP}
                      onChange={(e) => setNewIP(e.target.value)}
                    />
                    <button
                      onClick={handleAddIP}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition duration-300"
                    >
                      Add IP
                    </button>
                  </div>
                )}

                {!isAdmin && (
                  <p className="text-gray-400">
                    You must be an admin to add or remove IP addresses.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other tabs... */}
        {activeTab === 'ssh' && (
          <div className="bg-dark-card p-4 rounded-lg text-white">
            <h2 className="text-xl font-bold mb-2">SSH Keys</h2>
            <p>Manage your SSH keys here...  Coming soon in future update.</p>
          </div>
        )}
        {activeTab === 'api' && (
          <div className="bg-dark-card p-4 rounded-lg text-white">
            <h2 className="text-xl font-bold mb-2">API Keys</h2>
            <p>Manage your API keys here...  Coming soon in future update.</p>
          </div>
        )}
        {activeTab === 'login' && (
          <div className="bg-dark-card p-4 rounded-lg text-white">
            <h2 className="text-xl font-bold mb-2">Login History</h2>
            <p>
              Login History will go here. Coming soon in future update.
            </p>
          </div>
        )}
        {activeTab === 'access' && (
          <div className="bg-dark-card p-4 rounded-lg text-white">
            <h2 className="text-xl font-bold mb-2">Access Log</h2>
            <p>View your access log here...  Coming soon in future update.</p>
          </div>
        )}
      </div>

      {/* A simple "Saved!" popup. Could style more elaborately. */}
      {showSavePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-dark-card p-6 rounded-lg text-white max-w-xs w-full">
            <h2 className="text-lg font-bold mb-4">Saved!</h2>
            <p>Your changes were saved successfully.</p>
            <button
              onClick={closePopup}
              className="mt-4 bg-blue-600 px-4 py-2 rounded hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Account;